import React, { useEffect, useRef, useState } from "react";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { listBookmarks, addBookmark, removeBookmark } from "../lib/api";

export default function BookmarkButton({ problemId }) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!isAuthenticated || !problemId) {
      setSaved(false);
      return;
    }
    const myReq = ++reqIdRef.current;
    listBookmarks()
      .then((bs) => {
        if (myReq !== reqIdRef.current) return; // a newer request superseded us
        setSaved(bs.some((b) => b.problem_id === problemId));
      })
      .catch(() => {});
  }, [isAuthenticated, problemId]);

  const handleToggle = async () => {
    if (!isAuthenticated) {
      navigate("/signin", { state: { from: location.pathname } });
      return;
    }
    setBusy(true);
    try {
      if (saved) {
        await removeBookmark(problemId);
        setSaved(false);
      } else {
        await addBookmark(problemId);
        setSaved(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={busy}
      data-testid="bookmark-button"
      className={`inline-flex items-center gap-2 px-5 py-2.5 font-mono text-[11px] uppercase tracking-wide-2 transition-colors border ${
        saved
          ? "bg-ink-100 text-ink-900 border-ink-300"
          : "bg-bone text-ink-900 border-ink-300 hover:bg-ink-100"
      }`}
    >
      {saved ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
      {saved ? "Bookmarked" : "Bookmark"}
    </button>
  );
}
